/**
 * Special Order pay page (`/special-orders/:id/pay`).
 * The assigned agent reviews an admin-created special order and pays it
 * through the normal storefront checkout (QuickBooks card + PayPal).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Lock, ShieldCheck, ArrowLeft, Package } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { shopAPI, shopPaymentsAPI } from '@/lib/api';
import { tokenizeCardWithIntuit } from '@/lib/intuitTokenizer';
import useRecaptcha from '@/hooks/useRecaptcha';
import { getErrorMessage } from '@/lib/utils';
import PaymentMethodPicker from '@/components/shop/PaymentMethodPicker';
import PayPalPayButton from '@/components/shop/PayPalPayButton';

const money = (n) => (n === null || n === undefined ? '—' : `$${Number(n).toFixed(2)}`);

export default function SpecialOrderPayPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [providers, setProviders] = useState([]);
  const [intent, setIntent] = useState(null);
  const [method, setMethod] = useState('card');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const numberRef = useRef();
  const expMonthRef = useRef();
  const expYearRef = useRef();
  const cvcRef = useRef();
  const [name, setName] = useState('');
  const [postal, setPostal] = useState('');

  const { ready: captchaReady, execute: executeCaptcha } = useRecaptcha(
    intent?.recaptcha_site_key,
  );

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const r = await shopAPI.getSpecialOrder(id);
      setOrder(r.data.order);
      setProviders(r.data.providers || []);
      if (r.data.paid) { setLoading(false); return; }
      // Try to prepare the QB card intent (tolerate failure — PayPal still works).
      const intentR = await shopAPI.specialPreparePayment(id).catch((e) => ({ __error: e }));
      if (intentR && !intentR.__error) {
        setIntent(intentR.data);
      } else {
        setIntent(null);
        const pp = (r.data.providers || []).find((p) => p.id === 'paypal' && p.enabled);
        if (pp) setMethod('paypal');
      }
    } catch (e) {
      setLoadError(getErrorMessage(e, 'Could not load this order'));
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const paypalCfg = (providers.find((p) => p.id === 'paypal') || {});
  const paypalEnabled = !!paypalCfg.enabled;

  const refreshIntent = async () => {
    try { const r = await shopAPI.specialPreparePayment(id); setIntent(r.data); }
    catch (e) { setLoadError(getErrorMessage(e, 'Could not refresh — reload the page')); }
  };

  const submitCard = async () => {
    if (!intent) return;
    if (!captchaReady) return toast.error('Fraud check still loading — try again in a second');
    const number = numberRef.current?.value?.replace(/\s+/g, '');
    const expMonth = expMonthRef.current?.value;
    const expYear = expYearRef.current?.value;
    const cvc = cvcRef.current?.value;
    if (!number || !expMonth || !expYear || !cvc || !name || !postal) {
      return toast.error('Fill all card and billing fields');
    }
    setSubmitting(true);
    try {
      const tok = await tokenizeCardWithIntuit({
        tokenizationUrl: intent.tokenization_url,
        card: { number, expMonth, expYear, cvc, name, postal },
      });
      const recaptchaToken = await executeCaptcha('special_order_charge');
      const r = await shopAPI.specialCharge(id, {
        intent_id: intent.intent_id,
        payment_token: tok.value,
        recaptcha_token: recaptchaToken,
        snapshot_hash: intent.snapshot_hash,
      });
      const d = r.data || {};
      if (d.outcome === 'CAPTURED') {
        setResult({ order_id: d.order_id, charge_id: d.charge_id, brand: tok.card.brand, last4: tok.card.last4 });
        toast.success('Payment captured — thank you!');
      } else {
        toast.error(d.error ? `Payment declined: ${d.error}. Try another card.` : 'Payment declined by the issuer.');
        await refreshIntent();
      }
    } catch (err) {
      const code = err?.response?.status;
      toast.error(err?.response?.data?.detail || err?.message || 'Payment failed');
      if (code === 404 || code === 409) await refreshIntent();
    } finally { setSubmitting(false); }
  };

  const onPayPalApproved = async (data) => {
    if (data?.payment_status === 'paid') {
      setResult({ order_id: data.order_id, charge_id: data.provider_payment_id, brand: 'PayPal' });
      toast.success('Payment received via PayPal');
    } else if (data?.payment_status === 'processing') {
      toast.info('PayPal is still processing — we will email you the receipt shortly.');
    } else {
      toast.error('PayPal returned an unexpected status');
    }
  };

  if (loading) return (
    <div className="max-w-3xl mx-auto p-8 flex items-center gap-2 text-slate-500">
      <Loader2 size={16} className="animate-spin" /> Loading order…
    </div>
  );
  if (loadError) return (
    <div className="max-w-3xl mx-auto p-8" data-testid="special-order-error">
      <Card><CardContent className="p-6 text-rose-700 bg-rose-50 border border-rose-200 rounded">{loadError}</CardContent></Card>
    </div>
  );

  const paid = result || order?.payment_status === 'paid';
  if (paid) return (
    <div className="max-w-lg mx-auto p-8 text-center" data-testid="special-order-success">
      <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
        <CheckCircle2 className="text-emerald-600" size={32} />
      </div>
      <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment received</h1>
      <p className="text-slate-500 text-sm mb-6">
        Your special order <span className="font-mono">{order?.order_id}</span> is being processed.
        We&apos;ll keep you posted as it ships.
      </p>
      <div className="flex gap-2 justify-center">
        <Button variant="outline" onClick={() => navigate('/shop')}>Go to shop</Button>
        <Button onClick={() => navigate('/my-orders')} data-testid="special-order-view-orders">View my orders</Button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="special-order-pay-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/my-orders')} className="mb-4">
        <ArrowLeft size={13} className="mr-1.5" /> My orders
      </Button>
      <div className="flex items-center gap-2 mb-1">
        <Package size={20} className="text-[#0a1f47]" />
        <h1 className="text-2xl font-bold text-slate-900">{order?.title || 'Special Order'}</h1>
        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300">Pending payment</Badge>
      </div>
      <p className="text-sm text-slate-500 mb-6 flex items-center gap-1">
        <Lock size={12} /> Card data goes directly to Intuit — never touches KR servers.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Payment</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {!intent && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800" data-testid="card-unavailable-note">
                Card payments are temporarily unavailable. Please pay with PayPal below.
              </div>
            )}
            <PaymentMethodPicker providers={providers} value={method} onChange={setMethod} />

            {method === 'card' && (
              <div className="space-y-3 pt-2 border-t">
                <div>
                  <Label className="text-xs">Card number *</Label>
                  <Input placeholder="4111 1111 1111 1111" ref={numberRef} inputMode="numeric" autoComplete="cc-number" data-testid="cc-number" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label className="text-xs">Exp month *</Label><Input placeholder="12" maxLength={2} ref={expMonthRef} inputMode="numeric" data-testid="cc-exp-month" /></div>
                  <div><Label className="text-xs">Exp year *</Label><Input placeholder="2029" maxLength={4} ref={expYearRef} inputMode="numeric" data-testid="cc-exp-year" /></div>
                  <div><Label className="text-xs">CVV *</Label><Input placeholder="123" maxLength={4} ref={cvcRef} inputMode="numeric" data-testid="cc-cvc" /></div>
                </div>
                <div><Label className="text-xs">Name on card *</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="cc-name" /></div>
                <div><Label className="text-xs">Billing ZIP *</Label><Input value={postal} onChange={(e) => setPostal(e.target.value)} data-testid="cc-postal" /></div>
                <div className="pt-3 flex items-center justify-between border-t">
                  <span className="text-[11px] text-slate-400 flex items-center gap-1"><ShieldCheck size={11} /> reCAPTCHA v3 · PCI SAQ-A-EP</span>
                  <Button onClick={submitCard} disabled={submitting || !captchaReady || !intent} data-testid="special-order-pay-btn">
                    {submitting && <Loader2 size={13} className="mr-1.5 animate-spin" />}
                    Pay {money(order?.total)}
                  </Button>
                </div>
                {!intent && (
                  <p className="text-[11px] text-rose-600 text-center">
                    Credit card is temporarily unavailable — please choose PayPal above.
                  </p>
                )}
              </div>
            )}

            {(method === 'paypal' || method === 'paypal_paylater') && paypalEnabled && (
              <div className="pt-2 border-t space-y-2" data-testid="paypal-section">
                <p className="text-xs text-slate-500">You'll be redirected to PayPal to confirm this payment.</p>
                <PayPalPayButton
                  paypalConfig={paypalCfg.public_config}
                  method={method}
                  createOrderFn={async () => {
                    const r = await shopPaymentsAPI.specialPaypalCreate(id, { method });
                    return { paypal_order_id: r.data.paypal_order_id, order_id: r.data.order_id };
                  }}
                  onApproved={onPayPalApproved}
                  onError={(e) => toast.error(getErrorMessage(e, 'PayPal payment failed'))}
                />
              </div>
            )}

            <p className="text-[11px] text-slate-400 text-center pt-1">
              By paying you agree to our{' '}
              <Link to="/terms" target="_blank" className="underline hover:text-slate-600">Terms</Link>,{' '}
              <Link to="/privacy" target="_blank" className="underline hover:text-slate-600">Privacy Policy</Link>, and{' '}
              <Link to="/refund-policy" target="_blank" className="underline hover:text-slate-600">Refund &amp; Return Policy</Link>.
            </p>
          </CardContent>
        </Card>

        <Card className="lg:col-span-1 h-fit">
          <CardHeader><CardTitle className="text-base">Order Summary</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            {(order?.items || []).map((i, idx) => (
              <div key={idx} className="flex justify-between text-slate-700">
                <span className="truncate mr-2">{i.title} × {i.quantity}</span>
                <span className="font-mono">{money(i.line_total ?? i.unit_price * i.quantity)}</span>
              </div>
            ))}
            <div className="border-t pt-2 flex justify-between text-slate-500 text-xs">
              <span>Subtotal</span><span className="font-mono">{money(order?.subtotal)}</span>
            </div>
            <div className="flex justify-between text-slate-500 text-xs">
              <span>Shipping</span><span className="font-mono">{money(order?.shipping_amount)}</span>
            </div>
            <div className="border-t pt-2 flex justify-between font-bold text-lg text-slate-900">
              <span>Total</span><span className="font-mono" data-testid="special-order-total">{money(order?.total)}</span>
            </div>
            {order?.admin_notes && (
              <div className="border-t pt-2 text-xs text-slate-500">
                <span className="font-semibold text-slate-600">Note:</span> {order.admin_notes}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
